import React, { useState, useEffect, useMemo } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { Wrench, Beaker, Droplets, Zap, Waves, Sun, ChevronDown } from "lucide-react";
import SEO from "../components/SEO";

import FrictionLossCalculator   from "../components/tools/FrictionLossCalculator";
import NetworkFrictionCalculator from "../components/tools/NetworkFrictionCalculator";
import PipeVelocityCalculator   from "../components/tools/PipeVelocityCalculator";
import NPSHCalculator           from "../components/tools/NPSHCalculator";
import PumpPowerCalculator      from "../components/tools/PumpPowerCalculator";
import TDSConverter             from "../components/tools/TDSConverter";
import ROCalculator             from "../components/tools/ROCalculator";
import DosingCalculator         from "../components/tools/DosingCalculator";
import LSICalculator            from "../components/tools/LSICalculator";
import SDSICalculator           from "../components/tools/SDSICalculator";
import RainwaterCalculator      from "../components/tools/RainwaterCalculator";
import StorageSizingCalculator  from "../components/tools/StorageSizingCalculator";
import PoolTurnoverCalculator   from "../components/tools/PoolTurnoverCalculator";
import AmpsKwConverter          from "../components/tools/AmpsKwConverter";
import PlantPowerCalculator    from "../components/tools/PlantPowerCalculator";
import CableSizeCalculator     from "../components/tools/CableSizeCalculator";
import PVSolarCalculator       from "../components/tools/PVSolarCalculator";
import PumpSizingWorkflow      from "../components/tools/PumpSizingWorkflow";
import PurificationPlantSizing from "../components/tools/PurificationPlantSizing";
import SofteningResinSizing   from "../components/tools/SofteningResinSizing";
import GatedTool               from "../components/GatedTool";

// IDs of tools that require an approved email before the calculator is shown.
// Everything else is fully public — keeps the SEO engine intact.
const GATED_TOOL_IDS = new Set(["pump-sizing", "purification-plant", "softening-resin"]);

/**
 * /tools  —  Engineering calculators hub
 * --------------------------------------------------------------------
 *  Layout:
 *   · Hero — homepage register (navy + cyan two-line heading)
 *   · 4 tiles in a grid.  Clicking one expands a dropdown list of its
 *     calculators INSIDE the tile.  Selecting one of those names mounts
 *     that single calculator below.  Only one calculator visible at a
 *     time so the page never becomes a scroll-fest.
 *   · No two-toned strips, no count meta, no "X calculators across Y
 *     disciplines" filler.  Light theme throughout.
 * --------------------------------------------------------------------
 */

const ink   = "#0C2A40";
const cyan  = "#1FB6C1";
const paper = "#FAFAF7";

const GROUPS = [
  {
    id: "hydraulics",
    icon: Wrench,
    title: "Hydraulic",
    blurb: "Pipe sizing, velocity, friction loss, pump head & power, suction-line checks.",
    tools: [
      { id: "hazen-williams",   label: "Friction loss calculator",          Comp: FrictionLossCalculator },
      { id: "network-friction", label: "Network friction loss",              Comp: NetworkFrictionCalculator },
      { id: "pipe-velocity",    label: "Pipe velocity check",                Comp: PipeVelocityCalculator },
      { id: "pump-power",       label: "Pump power & head required",         Comp: PumpPowerCalculator },
      { id: "npsh-available",   label: "NPSH available",                     Comp: NPSHCalculator },
    ],
  },
  {
    id: "water-quality",
    icon: Beaker,
    title: "Water quality",
    blurb: "Conversions, chemical dosing, RO recovery and stability indices for any feed.",
    tools: [
      { id: "tds-ec",      label: "TDS ↔ EC converter",                  Comp: TDSConverter },
      { id: "ro-recovery", label: "RO recovery & brine flow",            Comp: ROCalculator },
      { id: "dosing",      label: "Chemical dosing",                     Comp: DosingCalculator },
      { id: "lsi",         label: "LSI — Langelier Saturation Index",    Comp: LSICalculator },
      { id: "sdsi",        label: "S&DSI — Stiff & Davis Stability",     Comp: SDSICalculator },
    ],
  },
  {
    id: "sizing-capacity",
    icon: Droplets,
    title: "Sizing & capacity",
    blurb: "Outcome-led sizing workflows — pump duty point, purification plant, storage and rainwater yield — assembled from the underlying calculators.",
    tools: [
      { id: "pump-sizing",       label: "Pump sizing — full duty-point workflow", Comp: PumpSizingWorkflow },
      { id: "purification-plant", label: "Purification plant sizing",              Comp: PurificationPlantSizing },
      { id: "softening-resin",   label: "Water softening resin sizing",            Comp: SofteningResinSizing },
      { id: "storage-sizing",    label: "Water storage sizing",                    Comp: StorageSizingCalculator },
      { id: "rainwater-yield",   label: "Rainwater harvest yield",                 Comp: RainwaterCalculator },
    ],
  },
  {
    id: "pool-spa",
    icon: Waves,
    title: "Pool & spa",
    blurb: "Turnover rate, pump sizing and circulation targets for pools and water features.",
    tools: [
      { id: "pool-turnover", label: "Pool turnover rate", Comp: PoolTurnoverCalculator },
    ],
  },
  {
    id: "electrical",
    icon: Zap,
    title: "Electrical",
    blurb: "Convert between current, kW and hp · estimate full-plant energy use and running cost.",
    tools: [
      { id: "amps-kw",     label: "Amps ↔ kW / hp converter",   Comp: AmpsKwConverter },
      { id: "cable-size",  label: "Cable size calculator",       Comp: CableSizeCalculator },
      { id: "plant-power", label: "Plant power & running cost", Comp: PlantPowerCalculator },
    ],
  },
  {
    id: "solar",
    icon: Sun,
    title: "Solar",
    blurb: "Size a PV array for a solar pump, or work out how many hours an installed array will run.",
    tools: [
      { id: "pv-solar", label: "PV array sizing for solar pumps", Comp: PVSolarCalculator },
    ],
  },
];

export default function Tools() {
  const { hash, pathname } = useLocation();
  const navigate = useNavigate();
  const params = useParams();

  // -- URL → state -----------------------------------------------------------
  // Routes recognised:
  //   /tools                              → first tile open, first calc selected
  //   /tools/:groupId                     → that tile open, its first calc selected
  //   /tools/:groupId/:toolId             → that tile open, that calc selected
  // Plus legacy #hash form (#tool-id) kept working so old shared links don't 404.
  const resolved = useMemo(() => {
    // 1. Param-based deep link
    if (params.groupId) {
      const g = GROUPS.find((x) => x.id === params.groupId);
      if (g) {
        if (params.toolId) {
          const t = g.tools.find((x) => x.id === params.toolId);
          if (t) return { groupId: g.id, toolId: t.id };
        }
        return { groupId: g.id, toolId: g.tools[0].id };
      }
    }
    // 2. Legacy hash deep link
    if (hash) {
      const id = hash.replace("#", "");
      for (const g of GROUPS) {
        if (g.id === id) return { groupId: g.id, toolId: g.tools[0].id };
        const t = g.tools.find((x) => x.id === id);
        if (t) return { groupId: g.id, toolId: t.id };
      }
    }
    // 3. Default landing
    return { groupId: "hydraulics", toolId: "hazen-williams" };
  }, [params.groupId, params.toolId, hash]);

  const [openId, setOpenId] = useState(resolved.groupId);
  const [selected, setSelected] = useState({ groupId: resolved.groupId, toolId: resolved.toolId });

  // Keep local state synced when the URL changes (back / forward, paste, etc.)
  useEffect(() => {
    setOpenId(resolved.groupId);
    setSelected({ groupId: resolved.groupId, toolId: resolved.toolId });
  }, [resolved.groupId, resolved.toolId]);

  // Quietly canonicalise legacy hash URLs to the new param form so what the
  // visitor sees in the address bar matches what they'd copy-paste to share.
  useEffect(() => {
    if (hash) {
      const target = `/tools/${resolved.groupId}/${resolved.toolId}`;
      if (pathname !== target) navigate(target, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hash]);

  const handleTileClick = (g) => {
    if (openId === g.id) {
      setOpenId(null);
      return;
    }
    setOpenId(g.id);
    // First calc in the group becomes the selection — and the URL updates.
    if (selected.groupId !== g.id) {
      const firstTool = g.tools[0].id;
      setSelected({ groupId: g.id, toolId: firstTool });
      navigate(`/tools/${g.id}/${firstTool}`);
    } else {
      navigate(`/tools/${g.id}/${selected.toolId}`);
    }
  };

  const handleToolPick = (groupId, toolId) => {
    setSelected({ groupId, toolId });
    // Push the new URL so back-button works and visitor can copy the address.
    navigate(`/tools/${groupId}/${toolId}`);
    // Smooth scroll to the active calculator.
    setTimeout(() => {
      document.getElementById("tool-active")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  };

  const activeGroup = GROUPS.find((g) => g.id === selected.groupId);
  const activeTool  = activeGroup?.tools.find((t) => t.id === selected.toolId);
  const ActiveComp  = activeTool?.Comp;

  // Build per-route SEO so each calculator URL gets its own title + meta.
  // Crawlers + visitors who paste the URL see the right page card.
  const seoProps = useMemo(() => {
    if (!activeGroup || !activeTool) {
      return {
        title: "Tools",
        description: "Hydraulic, water-quality, sizing, irrigation, pool, solar and electrical calculators for water professionals — built on industry-standard formulas.",
        path: "/tools",
      };
    }
    // Only override the default SEO when the URL actually carries a tool —
    // landing on bare /tools still shows the hub-level meta.
    if (!params.groupId) {
      return {
        title: "Tools",
        description: "Hydraulic, water-quality, sizing, irrigation, pool, solar and electrical calculators for water professionals — built on industry-standard formulas.",
        path: "/tools",
      };
    }
    return {
      title: `${activeTool.label} · ${activeGroup.title}`,
      description: `${activeTool.label} — ${activeGroup.blurb} Industry-standard formula, exportable PDF report, no sign-up required.`,
      path: `/tools/${activeGroup.id}/${activeTool.id}`,
      keywords: `${activeTool.label}, ${activeGroup.title} calculator, water systems, HidroVerse`,
    };
  }, [activeGroup, activeTool, params.groupId]);

  return (
    <div style={{ background: paper, color: ink }}>
      <SEO {...seoProps} />

      {/* ============================================================
          HERO
          ============================================================ */}
      <section
        className="px-6 md:px-12 lg:px-20 pt-28 md:pt-32 pb-12 md:pb-16"
        style={{ background: paper }}
        data-testid="tools-hero"
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-16 items-end">
            <div className="lg:col-span-7">
              <div
                className="font-mono uppercase mb-5"
                style={{ fontSize: "0.62rem", letterSpacing: "0.26em", color: cyan }}
              >
                Tools
              </div>
              <h1
                className="font-display"
                style={{
                  fontSize: "clamp(2rem, 4.5vw, 3.4rem)",
                  lineHeight: 1.06,
                  letterSpacing: "-0.02em",
                  fontWeight: 700,
                  color: ink,
                }}
                data-testid="tools-hero-heading"
              >
                Industry-standard calculators.<br />
                <span style={{ color: cyan }}>Real formulas. Real results.</span>
              </h1>
            </div>
            <div className="lg:col-span-5">
              <p
                className="font-light leading-relaxed"
                style={{ fontSize: "1rem", color: "#1A1F25" }}
              >
                Find the right calculator for the questions our specialists field weekly —
                pipe sizing, storage capacity, electrical conversions and water-quality
                checks. Sensible defaults, every field editable, every output in the units
                you work in.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          TILE GRID — open tile reveals dropdown list of calculators
          ============================================================ */}
      <section
        className="px-6 md:px-12 lg:px-20 pb-14 md:pb-20"
        style={{ background: paper }}
      >
        <div className="max-w-7xl mx-auto">
          <div
            className="grid sm:grid-cols-2 lg:grid-cols-6 gap-4 md:gap-5 items-start"
            data-testid="tools-tile-grid"
          >
            {GROUPS.map((g) => {
              const isOpen = openId === g.id;
              const Icon = g.icon;
              return (
                <div
                  key={g.id}
                  data-testid={`tools-tile-${g.id}`}
                  className="transition-all duration-300"
                  style={{
                    background: "#FFFFFF",
                    border: `1px solid ${isOpen ? cyan : "rgba(12,42,64,0.10)"}`,
                    borderRadius: 10,
                    boxShadow: isOpen
                      ? "0 16px 36px -16px rgba(31,182,193,0.30), 0 1px 2px rgba(12,42,64,0.04)"
                      : "0 1px 2px rgba(12,42,64,0.04), 0 10px 28px -16px rgba(12,42,64,0.10)",
                    overflow: "hidden",
                  }}
                >
                  {/* Tile header (clickable) */}
                  <button
                    type="button"
                    onClick={() => handleTileClick(g)}
                    className="w-full text-left p-5 md:p-6 transition-colors"
                    style={{ background: "transparent", color: ink }}
                    data-testid={`tools-tile-${g.id}-toggle`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <span
                        className="inline-flex items-center justify-center"
                        style={{
                          width: 40, height: 40, borderRadius: 8,
                          background: "rgba(31,182,193,0.10)",
                          color: ink,
                        }}
                      >
                        <Icon size={20} strokeWidth={1.75} />
                      </span>
                      <ChevronDown
                        size={18}
                        strokeWidth={1.5}
                        className="transition-transform duration-300 mt-2"
                        style={{
                          transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                          opacity: 0.55,
                          color: ink,
                        }}
                      />
                    </div>
                    <h3
                      className="font-display"
                      style={{
                        fontSize: "1.15rem",
                        lineHeight: 1.25,
                        letterSpacing: "-0.005em",
                        fontWeight: 600,
                        color: ink,
                      }}
                    >
                      {g.title}
                    </h3>
                    <p
                      className="mt-2 font-light leading-relaxed"
                      style={{ fontSize: "0.85rem", color: "rgba(12,42,64,0.7)" }}
                    >
                      {g.blurb}
                    </p>
                  </button>

                  {/* Calculator picker — only when tile is open */}
                  {isOpen && (
                    <div
                      className="border-t"
                      style={{ borderColor: "rgba(12,42,64,0.08)", background: "#FCFCFA" }}
                      data-testid={`tools-tile-${g.id}-list`}
                    >
                      <ul role="list" className="py-1">
                        {g.tools.map((t) => {
                          const active = selected.groupId === g.id && selected.toolId === t.id;
                          return (
                            <li key={t.id}>
                              <button
                                type="button"
                                onClick={() => handleToolPick(g.id, t.id)}
                                data-testid={`tools-pick-${t.id}`}
                                className="w-full text-left flex items-center justify-between px-5 md:px-6 py-3 transition-colors"
                                style={{
                                  background: active ? "rgba(31,182,193,0.08)" : "transparent",
                                  borderLeft: `3px solid ${active ? cyan : "transparent"}`,
                                }}
                                onMouseEnter={(e) => {
                                  if (!active) e.currentTarget.style.background = "rgba(12,42,64,0.04)";
                                }}
                                onMouseLeave={(e) => {
                                  if (!active) e.currentTarget.style.background = "transparent";
                                }}
                              >
                                <span
                                  className="font-display"
                                  style={{
                                    fontSize: "0.92rem",
                                    color: ink,
                                    fontWeight: active ? 600 : 500,
                                    letterSpacing: "-0.005em",
                                  }}
                                >
                                  {t.label}
                                </span>
                                <span
                                  className="font-mono uppercase ml-3"
                                  style={{
                                    fontSize: "0.58rem",
                                    letterSpacing: "0.2em",
                                    color: active ? cyan : "rgba(12,42,64,0.4)",
                                  }}
                                >
                                  {active ? "Open" : "Use"}
                                </span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============================================================
          ACTIVE CALCULATOR — single calculator, light theme, no filler
          ============================================================ */}
      <section
        id="tool-active"
        className="px-6 md:px-12 lg:px-20 pb-20 md:pb-24 scroll-mt-24"
        style={{ background: paper }}
        data-testid="tools-active"
      >
        <div className="max-w-7xl mx-auto">
          {ActiveComp ? (
            GATED_TOOL_IDS.has(activeTool.id) ? (
              <GatedTool
                toolId={activeTool.id}
                toolTitle={activeTool.label}
                seoDescription={`${activeTool.label} — request access for the full HidroVerse workflow tool. Internal team auto-approved.`}
                publicIntro={<GatedPublicIntro tool={activeTool} group={activeGroup} />}
              >
                <ActiveComp />
              </GatedTool>
            ) : (
              <ActiveComp />
            )
          ) : null}
        </div>
      </section>

      {/* ============================================================
          CTA
          ============================================================ */}
      <section className="cta-strip">
        <div className="csi">
          <div>
            <h3>Brief us on your water needs.</h3>
            <p className="csi-meta">
              For personalised assistance and solutions, contact our specialists.
            </p>
          </div>
          <Link to="/contact" className="hv-magnetic" style={{ justifyContent: "center" }} data-testid="tools-cta">
            Contact us <span aria-hidden>→</span>
          </Link>
        </div>
      </section>
    </div>
  );
}

// =============================================================================
// Public marketing intro shown above the gate for advanced workflow tools.
// Google indexes this content — the gate hides only the interactive calculator.
// Per-tool copy lives here so each gated tool has rich, distinct SEO content.
// =============================================================================
const GATED_INTRO_COPY = {
  "pump-sizing": {
    eyebrow: "Engineering workflow",
    headline: "Full pump duty-point sizing",
    body: [
      "Hydraulics (Hazen-Williams with collapsible sub-main and lateral tiers), NPSH context (altitude, fluid temperature, suction lift, suction-line friction), pump duty (hydraulic kW → shaft kW → electrical kW with next-standard IEC motor recommendation) and an optional solar PV section pre-filled from the workflow above — all in one workspace, no re-typing across tools.",
      "Output is a supplier-ready specification sheet you can paste straight into a tender pack: design flow, total dynamic head, motor size, NPSHr ceiling, site altitude and fluid-temperature reference, all on one PDF page.",
    ],
    bullets: [
      "Flow + critical-path friction + static head + safety margin → TDH",
      "Altitude + fluid temp + suction lift + suction friction → NPSHa",
      "Hydraulic → shaft → electrical kW with pump and motor efficiency curves",
      "Next-standard IEC motor recommendation (0.37 kW – 75 kW band)",
      "Optional solar PV array sizing pre-filled with the computed duty point",
      "Unified PDF export — one supplier-ready specification sheet",
    ],
    audience: "Used by lodge GMs sizing replacement pumps, municipal engineers specifying booster sets, and procurement officers building tender packs against a single defensible duty point.",
  },
  "purification-plant": {
    eyebrow: "Engineering workflow",
    headline: "Purification plant sizing",
    body: [
      "Translates the procurement question — \"what size plant do I need to make X m³/day?\" — into permeate flow, feed flow, reject flow and buffer-storage targets. Daily demand (direct or people × LPD) × operating window × recovery preset (RO, NF, UF, MF/UV or custom) compiles into a single supplier-ready plant specification.",
      "Pair the output with the pump sizing workflow to size the feed pump and the water storage sizing tool for the downstream buffer tank.",
    ],
    bullets: [
      "Daily demand input — direct, or derived from people × LPD",
      "Operating window (h/day) — defaults to 16 h for off-peak operation",
      "Recovery rate — your single input drives feed, reject and tank sizing",
      "Required permeate L/h, feed L/h, reject L/h",
      "Raw + clean water tank reserves sized off the same risk profile as the Water Storage tool",
      "Technology-aware energy hint (kWh per m³ permeate band)",
    ],
    audience: "Used by procurement officers specifying plants for lodges, municipal critical-line installations and hospital facilities — where the \"L/h permeate\" headline number drives the entire procurement spec.",
  },
  "softening-resin": {
    eyebrow: "Engineering workflow",
    headline: "Water softening resin sizing",
    body: [
      "Standard SAC (strong-acid cation) softener sizing. Translates daily water demand, raw water hardness and the chosen regeneration interval into litres of resin, daily salt usage, total regen water waste and an implied salt dose vs salt-efficiency profile — the four numbers a procurement officer needs to specify a softener and budget its lifetime running cost.",
      "Sanity checks every output against the industry 8 – 40 BV/h service rate band, the 80 – 240 g NaCl / L resin salt-dose band, and reports the sodium added to product water against the SANS 241 dietary limit.",
    ],
    bullets: [
      "Daily water demand + raw hardness (mg/L CaCO₃) → hardness load per regen cycle",
      "Regen frequency presets — 12 / 24 / 36 / 48 / 72 h or custom",
      "Working capacity by operating mode (high efficiency 32 g/L, standard 45 g/L, high capacity 60 g/L) or custom override",
      "Resin volume in L and ft³ with editable safety factor (default 10 %)",
      "Daily salt, brine and rinse-water consumption per the user's regen recipe",
      "Implied salt dose (g NaCl / L resin) and salt efficiency (g CaCO₃ removed per g NaCl)",
      "Service-rate sanity check against the 8 – 40 BV/h band",
      "Sodium added to product water (mg/L Na) for SANS 241 compliance checks",
    ],
    audience: "Used by lodge engineers comparing softener bids, municipal water-quality engineers planning regen-effluent disposal, and procurement officers building 10-year salt and waste-water cost forecasts.",
  },
};

function GatedPublicIntro({ tool, group }) {
  const copy = GATED_INTRO_COPY[tool.id] || {
    eyebrow: "Engineering workflow",
    headline: tool.label,
    body: [group.blurb],
    bullets: [],
    audience: "",
  };
  return (
    <section data-testid={`gated-intro-${tool.id}`}>
      <div
        className="font-mono uppercase mb-3"
        style={{ fontSize: "0.6rem", letterSpacing: "0.22em", color: "rgba(12,42,64,0.6)" }}
      >
        {copy.eyebrow}
      </div>
      <h1
        className="mb-5"
        style={{ fontSize: "clamp(1.75rem, 3.5vw, 2.6rem)", fontWeight: 700, color: "#0C2A40", lineHeight: 1.15, letterSpacing: "-0.01em" }}
      >
        {copy.headline}
      </h1>
      {copy.body.map((p, i) => (
        <p
          key={i}
          className="mb-4 font-light"
          style={{ fontSize: "1rem", color: "rgba(12,42,64,0.78)", lineHeight: 1.65, maxWidth: 760 }}
        >
          {p}
        </p>
      ))}

      {copy.bullets.length > 0 && (
        <div
          className="mt-6 p-5"
          style={{
            background: "#FFFFFF",
            border: "1px solid rgba(12,42,64,0.08)",
            borderRadius: 12,
            boxShadow: "0 1px 2px rgba(12,42,64,0.04), 0 10px 28px -16px rgba(12,42,64,0.10)",
          }}
        >
          <div
            className="font-mono uppercase mb-3"
            style={{ fontSize: "0.6rem", letterSpacing: "0.22em", color: "#1FB6C1", fontWeight: 700 }}
          >
            What the tool computes
          </div>
          <ul className="space-y-1.5" style={{ fontSize: "0.92rem", color: "rgba(12,42,64,0.85)" }}>
            {copy.bullets.map((b, i) => (
              <li key={i} className="flex gap-2">
                <span style={{ color: "#1FB6C1", fontWeight: 700 }}>·</span>
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {copy.audience && (
        <p
          className="mt-5 font-light italic"
          style={{ fontSize: "0.85rem", color: "rgba(12,42,64,0.6)", lineHeight: 1.6, maxWidth: 760 }}
        >
          {copy.audience}
        </p>
      )}
    </section>
  );
}

